import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Grid, GridItem } from 'foundations-components/transitional/components/Grid';
import { Image } from 'foundations-components/transitional/components/Image';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
import { useTrackMarketplaceEvent } from 'marketplace-ui-tracking';
import { getMarketplaceInteractionEvent } from 'marketplace-ui-tracking/events/marketplace';
import { useCallback, useEffect, useRef } from 'react';
import { Subscreens } from '../../constants/usage';
import { HERO_MEDIA_HEIGHT_DESKTOP, HERO_MEDIA_HEIGHT_MOBILE, MIN_HERO_SECTION_HEIGHT } from '../constants/dimensions';
import { HeroButton } from './HeroButton';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function HeroSection({
  buttonText,
  buttonUrl,
  heroPosition,
  imageUrl,
  isActive = false,
  listingId,
  onClick,
  resultType,
  secondaryButtonText,
  secondaryButtonUrl,
  slug,
  subtitle,
  title,
  videoId,
  openCopilot = false
}) {
  const trackMarketplaceEvent = useTrackMarketplaceEvent();
  const hasMedia = Boolean(imageUrl || videoId);
  const videoCurrentTimeRef = useRef(0);
  const {
    isMobile
  } = useResponsiveProps({
    isMobile: {
      xs: true,
      md: false
    }
  });
  const trackInteraction = useCallback(action => {
    trackMarketplaceEvent(getMarketplaceInteractionEvent({
      action,
      listingId,
      itemIndex: heroPosition,
      resultType,
      slug,
      subscreen: Subscreens.HERO_SECTION,
      videoId,
      videoWatchedDuration: videoCurrentTimeRef.current
    }));
  }, [heroPosition, listingId, resultType, slug, trackMarketplaceEvent, videoId]);
  useEffect(() => {
    if (!videoId || !isActive) return;
    const handleVideoEvent = message => {
      var _message$data$payload;
      if (message.origin !== 'https://play.hubspotvideo.com') return;
      if (!message.data) return;

      // Not all events have a currentTime payload, so we need to update the currentTime ref if it exists
      if (((_message$data$payload = message.data.payload) === null || _message$data$payload === void 0 ? void 0 : _message$data$payload.currentTime) !== undefined) {
        videoCurrentTimeRef.current = message.data.payload.currentTime;
      }
      switch (message.data.type) {
        case 'PLAYER_PLAY':
          trackInteraction('hero video played (not on autoplay)');
          break;
        case 'PLAYER_PAUSE':
        case 'PLAYER_ENDED':
          {
            const action = message.data.type === 'PLAYER_PAUSE' ? 'hero video paused' : 'hero video finished';
            trackInteraction(action);
            break;
          }
        default:
          break;
      }
    };
    window.addEventListener('message', handleVideoEvent);
    return () => window.removeEventListener('message', handleVideoEvent);
  }, [videoId, isActive, trackInteraction]);
  const handleHeroButtonClick = useCallback(() => {
    trackInteraction('hero button clicked');
    onClick === null || onClick === void 0 || onClick();
  }, [trackInteraction, onClick]);
  const InformationSection = () => /*#__PURE__*/_jsxs(Flex, {
    alignItems: "start",
    blockSize: "100%",
    flexDirection: "column",
    gap: "800",
    justifyContent: "center",
    marginBlockEnd: isMobile ? '800' : '0',
    children: [/*#__PURE__*/_jsxs(View, {
      children: [/*#__PURE__*/_jsx(Text, {
        as: "h2",
        marginBlockEnd: "400",
        typography: "heading-700",
        children: title
      }), /*#__PURE__*/_jsx(Text, {
        as: "p",
        typography: "body-300",
        children: subtitle
      })]
    }), /*#__PURE__*/_jsxs(Flex, {
      children: [/*#__PURE__*/_jsx(HeroButton, {
        buttonText: buttonText,
        buttonUrl: buttonUrl,
        onClick: handleHeroButtonClick,
        openCopilot: openCopilot,
        use: secondaryButtonText && secondaryButtonUrl ? 'primary' : 'secondary'
      }), secondaryButtonText && secondaryButtonUrl && /*#__PURE__*/_jsx(HeroButton, {
        buttonText: secondaryButtonText,
        buttonUrl: secondaryButtonUrl,
        openCopilot: openCopilot
      })]
    })]
  });
  const MediaSection = () => {
    if (!hasMedia) return null;
    return /*#__PURE__*/_jsx(Flex, {
      alignItems: "center",
      blockSize: isMobile ? HERO_MEDIA_HEIGHT_MOBILE : HERO_MEDIA_HEIGHT_DESKTOP,
      inlineSize: "100%",
      justifyContent: "center",
      marginBlockEnd: isMobile ? '400' : '0',
      children: videoId ? /*#__PURE__*/_jsx(Flex, {
        alignItems: "center",
        inlineSize: "100%",
        justifyContent: "center"
      }) : /*#__PURE__*/_jsx(Image, {
        alt: title,
        borderRadius: "400",
        inlineSize: "100%",
        objectFit: "contain",
        src: imageUrl
      })
    });
  };
  return /*#__PURE__*/_jsx(View, {
    backgroundColor: "fill-surface-default",
    "data-test-id": "hero-section",
    minBlockSize: MIN_HERO_SECTION_HEIGHT,
    children: isMobile ? /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      children: [/*#__PURE__*/_jsx(InformationSection, {}), hasMedia && /*#__PURE__*/_jsx(MediaSection, {})]
    }) : /*#__PURE__*/_jsxs(Grid, {
      blockSize: "100%",
      children: [/*#__PURE__*/_jsx(GridItem, {
        size: hasMedia ? 6 : 12,
        children: /*#__PURE__*/_jsx(InformationSection, {})
      }), hasMedia && /*#__PURE__*/_jsx(GridItem, {
        size: 6,
        children: /*#__PURE__*/_jsx(MediaSection, {})
      })]
    })
  });
}